import { AbstractControl, ValidationErrors } from '@angular/forms';
import { isImageFile, isVideoFile } from './helpers';

/**
 * FSR attachment batches are capped at 100MB.
 *
 * The browser only measures and warns. When the user opts in, the batch is
 * re-posted with compress=true and the backend does the shrinking (Pillow for
 * images, ffmpeg for video) — see api/utils/file_compression.py. Doing it
 * client-side would mean shipping ffmpeg.wasm and serving the app with
 * COOP/COEP headers.
 */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

export interface FileSizeBreakdown {
  total: number;
  images: { files: File[]; bytes: number };
  videos: { files: File[]; bytes: number };
  others: { files: File[]; bytes: number };
  /** True when nothing in the batch is a type the backend can shrink. */
  nothingCompressible: boolean;
}

export function totalSize(files: File[]): number {
  return (files || []).reduce((sum, file) => sum + (file?.size || 0), 0);
}

export function exceedsLimit(files: File[], limit: number = MAX_UPLOAD_BYTES): boolean {
  return totalSize(files) > limit;
}

/** Split a batch by what the backend can and cannot compress. */
export function categoriseFiles(files: File[]): FileSizeBreakdown {
  const images: File[] = [];
  const videos: File[] = [];
  const others: File[] = [];

  (files || []).forEach((file) => {
    if (isImageFile(file)) {
      images.push(file);
    } else if (isVideoFile(file)) {
      videos.push(file);
    } else {
      others.push(file);
    }
  });

  return {
    total: totalSize(files),
    images: { files: images, bytes: totalSize(images) },
    videos: { files: videos, bytes: totalSize(videos) },
    others: { files: others, bytes: totalSize(others) },
    nothingCompressible: images.length === 0 && videos.length === 0,
  };
}

export function formatFileSize(bytes: number): string {
  if (!bytes) {
    return '0 Bytes';
  }
  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const index = Math.floor(Math.log(bytes) / Math.log(1024));
  const clamped = Math.min(index, units.length - 1);
  return `${Math.round((bytes / Math.pow(1024, clamped)) * 100) / 100} ${units[clamped]}`;
}

/**
 * 'HH:MM:SS' difference between two time inputs, treating an end at or before
 * the start as the next day (a 22:00 -> 02:00 visit is 4 hours, not -20).
 * Returns '' when either side is missing or malformed.
 */
export function workedHours(timeIn: string | null, timeOut: string | null): string {
  const start = parseTimeToSeconds(timeIn);
  const end = parseTimeToSeconds(timeOut);
  if (start === null || end === null) {
    return '';
  }
  let elapsed = end - start;
  if (elapsed <= 0) {
    elapsed += 24 * 3600;
  }
  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * Form-level validator: Time In and Time Out must be supplied together.
 *
 * Mirrors the backend's check in FSRWriteSerializer.validate, so the user finds
 * out before submitting rather than from a 400.
 */
export function timePairValidator(group: AbstractControl): ValidationErrors | null {
  const timeIn = group.get('time_in')?.value;
  const timeOut = group.get('time_out')?.value;
  return !!timeIn === !!timeOut ? null : { timePair: true };
}

function parseTimeToSeconds(value: string | null): number | null {
  if (!value) {
    return null;
  }
  // <input type="time"> yields HH:MM without step, HH:MM:SS with step="1".
  const parts = value.split(':').map((part) => Number(part));
  if (parts.length < 2 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }
  const [hours, minutes, seconds = 0] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}
